/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useState } from "react";
import LayoutProtectedRoutes from "@/src/containers/Layouts/LayoutProtectedRoutes";
import LegalbirdLoader from "@/src/components/ContentLoader/LegalbirdLoader";
import { Navigate } from "react-router";
import { getCaseLink, getProductData, getProductNameBy } from "@/src/services/productService";
import _cloneDeep from "lodash/cloneDeep";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchResource, queryKeys, updateResource } from "@/src/services/reactQuery/reactQueryService";
import { useInquiryToCustomers } from "@/src/hooks/queries/useInquiryToCustomers";
import useProductParams from "@/src/hooks/useProductParams";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import { InquiryToCustomerStatus } from "@/src/types/Entities/InquiryToCustomer";
import { STATUS_ADMINISTRATIVE_DECISION_ISSUED } from "@/src/services/administrativeLawLitigationCase/leadStatus";
import AdministrativeDecisionFeedbackForm from "@/src/components/Form/AbstractAdministrativeLawLitigationCase/AdministrativeDecisionFeedbackForm/AdministrativeDecisionFeedbackForm";
import ProductFeedbackPage from "@/src/components/Serviceworld/Feedback/ProductFeedbackPage";
import FeedbackConfirmationPage from "@/src/components/Serviceworld/Feedback/FeedbackConfirmationPage";

const AdministrativeDecisionFeedbackPage = () => {
  const { productName, productId } = useProductParams();
  const productClassName = getProductNameBy("urlPartShort", productName);
  const productApiUri = getProductData(productClassName, "apiUri");
  const { data: product, isLoading } = useQuery({
    queryKey: queryKeys.item(productApiUri, productId),
    queryFn: () => fetchResource(productApiUri, productId),
  });
  const [isLoadingInternal, setIsLoadingInternal] = useState(false);
  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: updateResource,
    onSuccess: async (data, variables) => {
      queryClient.setQueryData(queryKeys.item(variables.uri, variables.id), data);
    },
  });

  const inquiryFilters = {
    identifier: "administrativeDecisionFeedbackInquiry",
    productId: productId,
    "status[0]": InquiryToCustomerStatus.STATUS_OPEN,
    "status[1]": InquiryToCustomerStatus.STATUS_OVERDUE,
    "status[2]": InquiryToCustomerStatus.STATUS_IN_PROGRESS,
  };
  const { data: inquiries } = useInquiryToCustomers(inquiryFilters, !!product);

  if (isLoading || !product || !inquiries) {
    return (
      <LayoutProtectedRoutes>
        <LegalbirdLoader />
      </LayoutProtectedRoutes>
    );
  }

  if (!!product.administrativeDecisionStatusClient || product.leadStatus !== STATUS_ADMINISTRATIVE_DECISION_ISSUED) {
    return <FeedbackConfirmationPage caseLink={getCaseLink(product)} />;
  }

  const currentInquiryToCustomer = inquiries[0];

  if (!currentInquiryToCustomer || currentInquiryToCustomer.identifier !== "administrativeDecisionFeedbackInquiry") {
    return <Navigate to={getCaseLink(product)} />;
  }

  const getHintText = (product: AbstractCase) => {
    return (
      <>
        <strong>
          {product.administrativeProceedingsLetterSentDate !== null
            ? "Die Behörde hat auf unser Schreiben mit einem Bescheid reagiert."
            : "Zu Ihrem Verfahren liegt ein Bescheid der Behörde vor."}
        </strong>
        <br />
        Wir benötigen als nächstes Ihre Rückmeldung darüber, wie Sie weiter vorgehen möchten.
      </>
    );
  };

  const initialValues = {
    administrativeDecisionStatusClient: null,
    administrativeDecisionFeedbackClient: null,
    additionalCustomerNote: "",
  };

  const handleSubmit = async ({ values }: { values: AbstractCase }) => {
    setIsLoadingInternal(true);

    const updateData = _cloneDeep(values);

    if (updateData.additionalCustomerNote === "") {
      updateData.additionalCustomerNote = null;
    }

    if (updateData.administrativeDecisionStatusClient === "fullyAgreed") {
      updateData.administrativeDecisionFeedbackClient = "accept";
    }

    await updateMutation.mutateAsync(
      {
        uri: getProductData(product.name, "apiUri"),
        id: product.id,
        data: updateData,
      },
      {
        // This marks the inquiry as done in BE subscriber, so collections should refetch
        onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.collections("/inquiry_to_customers") }),
      },
    );

    setIsLoadingInternal(false);
  };

  return (
    <ProductFeedbackPage hintText={getHintText(product)} initialValues={initialValues} submitAction={handleSubmit}>
      <AdministrativeDecisionFeedbackForm product={product} isLoading={isLoadingInternal} />
    </ProductFeedbackPage>
  );
};

export default AdministrativeDecisionFeedbackPage;
